import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useCreateMenuItem, useUpdateMenuItem } from '../../../api/mutations/menu.mutations';
import { useCategories } from '../../../api/queries/menu.queries';
import type { MenuItem } from '../../../api/queries/menu.queries';

interface ItemEditModalProps {
  item?: MenuItem;
  onClose: () => void;
  onSuccess: () => void;
}

function ItemEditModal({ item, onClose, onSuccess }: ItemEditModalProps) {
  const { t, i18n } = useTranslation();
  const { data: categories } = useCategories();
  const createMutation = useCreateMenuItem();
  const updateMutation = useUpdateMenuItem(item?._id || '');

  // Form state
  const [categoryId, setCategoryId] = useState(item?.categoryId || '');
  const [nameEn, setNameEn] = useState(item?.name.en || '');
  const [nameTh, setNameTh] = useState(item?.name.th || '');
  const [descEn, setDescEn] = useState(item?.description?.en || '');
  const [descTh, setDescTh] = useState(item?.description?.th || '');
  const [basePrice, setBasePrice] = useState(item?.basePrice?.toString() || '');
  const [imageUrl, setImageUrl] = useState(item?.imageUrl || '');
  const [isAvailable, setIsAvailable] = useState(item?.isAvailable ?? true);
  const [isRecommended, setIsRecommended] = useState(item?.isRecommended ?? false);
  const [tags, setTags] = useState(item?.tags?.join(', ') || '');

  // Size options
  const [hasSmall, setHasSmall] = useState(item?.availableSizes?.includes('small') ?? true);
  const [hasMedium, setHasMedium] = useState(item?.availableSizes?.includes('medium') ?? true);
  const [hasLarge, setHasLarge] = useState(item?.availableSizes?.includes('large') ?? true);
  const [smallPrice, setSmallPrice] = useState(item?.sizePricing?.small?.toString() || '');
  const [mediumPrice, setMediumPrice] = useState(item?.sizePricing?.medium?.toString() || '');
  const [largePrice, setLargePrice] = useState(item?.sizePricing?.large?.toString() || '');

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (item) {
      setCategoryId(item.categoryId);
      setNameEn(item.name.en);
      setNameTh(item.name.th);
      setDescEn(item.description?.en || '');
      setDescTh(item.description?.th || '');
      setBasePrice(item.basePrice.toString());
      setImageUrl(item.imageUrl || '');
      setIsAvailable(item.isAvailable);
      setIsRecommended(item.isRecommended ?? false);
      setTags(item.tags?.join(', ') || '');

      setHasSmall(item.availableSizes?.includes('small') ?? false);
      setHasMedium(item.availableSizes?.includes('medium') ?? false);
      setHasLarge(item.availableSizes?.includes('large') ?? false);
      setSmallPrice(item.sizePricing?.small?.toString() || '');
      setMediumPrice(item.sizePricing?.medium?.toString() || '');
      setLargePrice(item.sizePricing?.large?.toString() || '');
    }
  }, [item]);

  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!categoryId) {
      newErrors.categoryId = 'Category is required';
    }
    if (!nameEn.trim()) {
      newErrors.nameEn = 'English name is required';
    }
    if (!nameTh.trim()) {
      newErrors.nameTh = 'Thai name is required';
    }
    if (!basePrice || parseFloat(basePrice) <= 0) {
      newErrors.basePrice = 'Valid price is required';
    }
    if (!hasSmall && !hasMedium && !hasLarge) {
      newErrors.sizes = 'Select at least one size';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      const availableSizes: ('small' | 'medium' | 'large')[] = [];
      if (hasSmall) availableSizes.push('small');
      if (hasMedium) availableSizes.push('medium');
      if (hasLarge) availableSizes.push('large');

      const sizePricing: { small?: number; medium?: number; large?: number } = {};
      if (hasSmall && smallPrice) sizePricing.small = parseFloat(smallPrice);
      if (hasMedium && mediumPrice) sizePricing.medium = parseFloat(mediumPrice);
      if (hasLarge && largePrice) sizePricing.large = parseFloat(largePrice);

      const payload = {
        categoryId,
        name: { en: nameEn, th: nameTh },
        description: descEn || descTh ? { en: descEn, th: descTh } : undefined,
        basePrice: parseFloat(basePrice),
        availableSizes,
        sizePricing: Object.keys(sizePricing).length > 0 ? sizePricing : undefined,
        imageUrl: imageUrl || undefined,
        isAvailable,
        isRecommended,
        tags: tags
          .split(',')
          .map((t: string) => t.trim())
          .filter((t: string) => t.length > 0),
      };

      if (item) {
        await updateMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save menu item:', error);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-card z-10">
          <h2 className="text-lg font-bold">
            {item ? t('menu.editItem') : t('menu.createItem')}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('menu.category')} <span className="text-destructive">*</span>
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className={`w-full rounded-lg border bg-background px-3 py-2 text-sm ${
                errors.categoryId ? 'border-destructive' : ''
              }`}
            >
              <option value="">{t('menu.category')}...</option>
              {categories?.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {getLangValue(cat.name)}
                </option>
              ))}
            </select>
            {errors.categoryId && (
              <p className="text-xs text-destructive mt-1">{errors.categoryId}</p>
            )}
          </div>

          {/* Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">
                Name (EN) <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={nameEn}
                onChange={(e) => setNameEn(e.target.value)}
                className={`w-full rounded-lg border bg-background px-3 py-2 text-sm ${
                  errors.nameEn ? 'border-destructive' : ''
                }`}
                placeholder="Mango Green Tea"
              />
              {errors.nameEn && (
                <p className="text-xs text-destructive mt-1">{errors.nameEn}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Name (TH) <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={nameTh}
                onChange={(e) => setNameTh(e.target.value)}
                className={`w-full rounded-lg border bg-background px-3 py-2 text-sm ${
                  errors.nameTh ? 'border-destructive' : ''
                }`}
                placeholder="ชาเขียวมะม่วง"
              />
              {errors.nameTh && (
                <p className="text-xs text-destructive mt-1">{errors.nameTh}</p>
              )}
            </div>
          </div>

          {/* Description */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Description (EN)</label>
              <textarea
                value={descEn}
                onChange={(e) => setDescEn(e.target.value)}
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                rows={2}
                placeholder="Fresh mango with green tea..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description (TH)</label>
              <textarea
                value={descTh}
                onChange={(e) => setDescTh(e.target.value)}
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                rows={2}
                placeholder="ชาเขียวผสมมะม่วงสด..."
              />
            </div>
          </div>

          {/* Base Price */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Base Price <span className="text-destructive">*</span>
            </label>
            <input
              type="number"
              value={basePrice}
              onChange={(e) => setBasePrice(e.target.value)}
              className={`w-full rounded-lg border bg-background px-3 py-2 text-sm ${
                errors.basePrice ? 'border-destructive' : ''
              }`}
              placeholder="50"
              min="0"
              step="0.01"
            />
            {errors.basePrice && (
              <p className="text-xs text-destructive mt-1">{errors.basePrice}</p>
            )}
          </div>

          {/* Sizes */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {t('pos.size')} <span className="text-destructive">*</span>
            </label>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="hasSmall"
                  checked={hasSmall}
                  onChange={(e) => setHasSmall(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="hasSmall" className="text-sm flex-1">
                  {t('pos.small')}
                </label>
                {hasSmall && (
                  <input
                    type="number"
                    value={smallPrice}
                    onChange={(e) => setSmallPrice(e.target.value)}
                    className="w-24 rounded border bg-background px-2 py-1 text-sm"
                    placeholder="+0"
                    min="0"
                    step="0.01"
                  />
                )}
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="hasMedium"
                  checked={hasMedium}
                  onChange={(e) => setHasMedium(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="hasMedium" className="text-sm flex-1">
                  {t('pos.medium')}
                </label>
                {hasMedium && (
                  <input
                    type="number"
                    value={mediumPrice}
                    onChange={(e) => setMediumPrice(e.target.value)}
                    className="w-24 rounded border bg-background px-2 py-1 text-sm"
                    placeholder="+10"
                    min="0"
                    step="0.01"
                  />
                )}
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="hasLarge"
                  checked={hasLarge}
                  onChange={(e) => setHasLarge(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <label htmlFor="hasLarge" className="text-sm flex-1">
                  {t('pos.large')}
                </label>
                {hasLarge && (
                  <input
                    type="number"
                    value={largePrice}
                    onChange={(e) => setLargePrice(e.target.value)}
                    className="w-24 rounded border bg-background px-2 py-1 text-sm"
                    placeholder="+20"
                    min="0"
                    step="0.01"
                  />
                )}
              </div>
            </div>
            {errors.sizes && (
              <p className="text-xs text-destructive mt-1">{errors.sizes}</p>
            )}
          </div>

          {/* Image URL */}
          <div>
            <label className="block text-sm font-medium mb-1">Image URL</label>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
              placeholder="https://..."
            />
            <p className="text-xs text-muted-foreground mt-1">
              Image upload with Cloudflare R2 coming soon
            </p>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium mb-1">Tags</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
              placeholder="seasonal, popular, new (comma-separated)"
            />
          </div>

          {/* Flags */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isAvailable"
                checked={isAvailable}
                onChange={(e) => setIsAvailable(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="isAvailable" className="text-sm font-medium">
                {t('menu.active')}
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isRecommended"
                checked={isRecommended}
                onChange={(e) => setIsRecommended(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="isRecommended" className="text-sm font-medium">
                Recommended
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t sticky bottom-0 bg-card">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-lg border bg-background px-4 py-2 text-sm font-medium hover:bg-secondary transition-colors"
              disabled={isLoading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ItemEditModal;
